/**
 * FactorRadar — 5-factor radar chart for a single stock + GPT rationale.
 */
import React, { useState, useEffect } from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from 'recharts';
import { Sparkles, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { factorMetaSingular, scoreColor, timeAgo } from './utils';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FactorRadar = ({ pick }) => {
  const { authFetch } = useAuth();
  const [rationale, setRationale] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Reset when pick changes; use embedded rationale if present
  useEffect(() => {
    if (!pick) return;
    setError(null);
    if (pick.rationale) {
      setRationale({
        text: pick.rationale,
        model: pick.rationale_model,
        generated_at: pick.rationale_generated_at,
      });
    } else {
      setRationale(null);
    }
  }, [pick?.symbol, pick?.rationale]);

  const generateRationale = async (force = false) => {
    if (!pick?.symbol) return;
    setGenerating(true);
    setError(null);
    try {
      const r = await authFetch(
        `${API}/api/alphascout/rationale/${pick.symbol}/generate?wait=true&force=${force}`,
        { method: 'POST' }
      );
      const d = await r.json();
      if (r.ok && d.rationale) {
        setRationale({ text: d.rationale, model: d.model, generated_at: d.generated_at });
      } else {
        setError(d.detail || d.error || 'Generation failed');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!pick || !pick.factor_scores) {
    return (
      <div className="bg-surface border border-border p-8 text-center text-sm text-muted-foreground">
        Click a pick to view factor radar.
      </div>
    );
  }

  const data = Object.entries(pick.factor_scores).map(([k, v]) => ({
    factor: factorMetaSingular[k]?.label || k,
    score: v,
    full: 100,
  }));

  const details = pick.factor_details || {};

  return (
    <div className="bg-surface border border-border p-4" data-testid="factor-radar">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-bold text-base text-foreground">{pick.symbol}</h3>
          <p className="text-xs text-muted-foreground">{pick.name} · {pick.sector}</p>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">
            Composite
          </div>
          <span
            className={`inline-flex items-center px-2 py-1 rounded-sm text-sm font-bold border ${scoreColor(
              pick.composite_score
            )}`}
            title="Composite = weighted average of Value, Quality, Momentum, Technical, Smart-$"
          >
            {Number(pick.composite_score).toFixed(1)}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Radar chart */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Factor Radar
          </div>
          <div className="h-[260px] -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={data} outerRadius="75%">
                <PolarGrid stroke="#30363d" />
                <PolarAngleAxis dataKey="factor" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 9 }} stroke="#30363d" />
                <Radar name={pick.symbol} dataKey="score" stroke="#fbbf24" fill="#fbbf24" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Rationale */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-accent" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                AI Rationale
              </span>
            </div>
            {rationale ? (
              <button
                onClick={() => generateRationale(true)}
                disabled={generating}
                className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="rationale-regen-btn"
              >
                <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
                Regen
              </button>
            ) : (
              <button
                onClick={() => generateRationale(false)}
                disabled={generating}
                className="text-[11px] px-2 py-1 bg-accent/10 text-accent border border-accent/30 hover:bg-accent/20 disabled:opacity-50 flex items-center gap-1"
                data-testid="rationale-generate-btn"
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {generating ? 'Generating...' : 'Explain'}
              </button>
            )}
          </div>
          {rationale ? (
            <div>
              <p className="text-xs text-foreground leading-relaxed" data-testid="rationale-text">
                {rationale.text}
              </p>
              <div className="text-[9px] text-muted-foreground mt-1.5 flex items-center gap-2">
                <span>{rationale.model}</span>
                {rationale.generated_at && <span>· {timeAgo(rationale.generated_at)}</span>}
              </div>
            </div>
          ) : error ? (
            <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/30 p-2">
              {error.length > 200 ? error.substring(0, 200) + '...' : error}
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground italic">
              Click "Explain" to generate an AI-powered 2-3 sentence rationale explaining why this pick scored high.
            </div>
          )}
        </div>

        {/* Factor details — key drivers */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Key Drivers
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2 text-[11px]">
            {Object.entries(details).map(([factor, info]) => {
              if (!info || typeof info !== 'object') return null;
              const score = pick.factor_scores?.[factor];
              return (
                <div key={factor} className="bg-surface-highlight rounded-sm p-2 border border-border/50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                      {factorMetaSingular[factor]?.label || factor}
                    </span>
                    <span className="font-mono font-bold" style={{ color: factorMetaSingular[factor]?.color }}>
                      {Math.round(score)}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {Object.entries(info).slice(0, 3).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">{k.replace(/_/g, ' ')}</span>
                        <span className="font-mono text-foreground">
                          {typeof v === 'number' ? v.toFixed(2) : String(v)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FactorRadar;
